import {css} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ParentStorableData} from 'constants/SampleConstants';
import FolderSelector from 'file-browser/FolderSelector';
import {InstrumentServiceDetailsLoader} from 'instruments/connections/ConnectionConstants';
import * as RegistryUtil from 'registry/RegistryUtil';
import ChangeParentStorableButton from 'samples/storables/ChangeParentStorableButton';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip from 'ui-library/bs/Tooltip';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import TimezonePickerDropdown from 'ui-library/dropdowns/TimezonePickerDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Menu from 'ui-library/menus/Menu';

import {AutomationOutputFileConfigGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileConfigGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {
  getLocationPickerTooltipText,
  locationPickerStyles,
  useLocationQuery,
  useStorableSchemaQuery,
} from './AutomationOutputFileLocationPicker';
import {timezonePickerStyles} from './AutomationOutputFileTimezonePicker';
import {
  isFolderRequiredForOutputFile,
  isLocationRequiredForOutputFile,
} from './isOutputFileMissingFolderOrLocation';
import {
  skipRowsStyles,
  SKIP_ROWS_WITH_ERRORS_TOOLTIP,
  SKIP_ROWS_WITH_ERRORS_LABEL,
} from './SkipRowsWithErrorsInput';
import UploadAutomationOutputFileButton from './UploadAutomationOutputFileButton';

interface BaseRunAutomationOutputFileUploaderProps {
  runId: string | null;
  configName: string | null;
  automationOutputFileConfig?: AutomationOutputFileConfigGraphQL | null;
  locked: boolean;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFileGraphQL>) => void;
  previousFileNames: Set<string>;
  disabled?: boolean;
  isConnectionRun: boolean;
  connectionId: string | null;
  instrumentServiceDetails: InstrumentServiceDetailsLoader;
}

/**
 * This component renders an upload button for uploading blobs to create output files.
 *
 * For Connect runs, it also adds options for setting "skip rows with errors", folder, location, and timezone.
 * This is done so the AOP can be processed immediately after the file is imported.
 */
const RunAutomationOutputFileUploader = (
  props: BaseRunAutomationOutputFileUploaderProps
): JSX.Element | null => {
  // Set default value of skip rows with errors checkbox based on whether file chunking is enabled,
  // since we only show the checkbox when file chunking is enabled.
  const [skipRowsWithErrors, setSkipRowsWithErrors] = useState<boolean>(
    BenchlingConfig.ENABLE_LAB_AUTOMATION_FILE_CHUNKING
  );
  const [folderId, setFolderId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);

  const disabled = props.disabled || props.locked || !props.runId;
  const isFolderRequired = Boolean(
    props.automationOutputFileConfig && isFolderRequiredForOutputFile(props.automationOutputFileConfig)
  );
  const isLocationRequired = Boolean(
    props.automationOutputFileConfig && isLocationRequiredForOutputFile(props.automationOutputFileConfig)
  );
  const shouldDisableForConnection =
    props.isConnectionRun && ((isFolderRequired && !folderId) || (isLocationRequired && !locationId));
  const disabledTooltip = isFolderRequired && !folderId ? 'Must select a folder.' : 'Must select a location.';
  const storableSchema = useStorableSchemaQuery(props.automationOutputFileConfig);
  const location = useLocationQuery(locationId);
  return (
    <div>
      <Row
        className="u-mini-spacing-top"
        bufferRowReset={true}
        dataTestAttrs={{component: 'RunAutomationOutputFileUploader', element: 'root'}}
      >
        <Col md={3}>
          <span className="fieldLabel-sublabel">Output</span>
        </Col>
        <Col md={9}>
          <UploadAutomationOutputFileButton
            runId={props.runId}
            configName={props.configName}
            automationOutputFileConfig={props.automationOutputFileConfig}
            disabled={disabled || shouldDisableForConnection}
            disabledTooltip={shouldDisableForConnection ? disabledTooltip : undefined}
            isConnectionRun={props.isConnectionRun}
            connectionId={props.connectionId}
            instrumentServiceDetails={props.instrumentServiceDetails}
            updateAutomationOutputFiles={props.updateAutomationOutputFiles}
            previousFileNames={props.previousFileNames}
            skipRowsWithErrors={skipRowsWithErrors}
            folderId={folderId}
            locationId={locationId}
            timezone={timezone}
          />
        </Col>
      </Row>
      {props.isConnectionRun && (
        <div>
          {BenchlingConfig.ENABLE_LAB_AUTOMATION_FILE_CHUNKING && (
            <Row className="u-mini-spacing-top" bufferRowReset={true}>
              <Col md={3} />
              <Col md={9}>
                <div className={css(skipRowsStyles.showErrorsCheckboxRow)}>
                  <CheckboxInput
                    id={`skip-rows-with-errors-checkbox-${props.runId}`}
                    className={css(skipRowsStyles.showErrorsCheckbox)}
                    label={SKIP_ROWS_WITH_ERRORS_LABEL}
                    checked={skipRowsWithErrors}
                    disabled={disabled}
                    onChange={() => {
                      setSkipRowsWithErrors(!skipRowsWithErrors);
                    }}
                  />
                  <Tooltip title={SKIP_ROWS_WITH_ERRORS_TOOLTIP}>
                    <BenchlingIcon className={css(skipRowsStyles.infoIcon)} icon="more-info" />
                  </Tooltip>
                </div>
              </Col>
            </Row>
          )}
          {isFolderRequired && (
            <Row className="u-mini-spacing-top" bufferRowReset={true}>
              <Col md={3}>
                <span className="fieldLabel-sublabel">Folder</span>
              </Col>
              <Col md={9}>
                <FolderSelector
                  buttonSize={ButtonSize.MINI}
                  enableRedesign={true}
                  showInventory={true}
                  showRegistries={!BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES}
                  readOnly={disabled}
                  value={folderId}
                  onChange={(e: {target: {value: string}}) => {
                    const {folderId} = RegistryUtil.parseFolderValue(e.target.value);
                    setFolderId(folderId);
                  }}
                  unsetOnFetchFail={true}
                  noFolderText="Choose folder for imported files"
                />
              </Col>
            </Row>
          )}
          {isLocationRequired && (
            <Row className="u-mini-spacing-top" bufferRowReset={true}>
              <Col md={3}>
                <span className="fieldLabel-sublabel">Location</span>
              </Col>
              <Col md={9}>
                <ChangeParentStorableButton
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  buttonSize={ButtonSize.MINI}
                  className={css(locationPickerStyles.changeParentStorableButton)}
                  icon={locationId ? undefined : ModelTypeConstants.LOCATION}
                  buttonText={location ? location.name : 'Choose location'}
                  fullWidth={true}
                  isWritable={!disabled && storableSchema != null && storableSchema !== 'isInaccessible'}
                  onSetParentStorable={(data: ParentStorableData) => {
                    setLocationId(data.id);
                  }}
                  storableSchema={storableSchema !== 'isInaccessible' ? storableSchema : null}
                  titleText={getLocationPickerTooltipText(storableSchema)}
                  parentStorableTypesOverride={[ModelTypeConstants.LOCATION]}
                />
              </Col>
            </Row>
          )}
          {props.automationOutputFileConfig && props.automationOutputFileConfig.canConfigureTimezone && (
            <Row className="u-mini-spacing-top" bufferRowReset={true}>
              <Col md={3}>
                <span className="fieldLabel-sublabel">Default Timezone</span>
                <Tooltip title="Timezone to use for date and datetime fields if not specified in spreadsheet">
                  <BenchlingIcon className={css(timezonePickerStyles.infoIcon)} icon="more-info" />
                </Tooltip>
              </Col>
              <Col md={9}>
                <TimezonePickerDropdown
                  disabled={disabled}
                  value={timezone}
                  onChange={(timezone: string): void => {
                    setTimezone(timezone);
                  }}
                  menuProps={{
                    height: Menu.HEIGHTS.MEDIUM,
                    width: Menu.WIDTHS.FULL,
                  }}
                />
              </Col>
            </Row>
          )}
        </div>
      )}
    </div>
  );
};

export default RunAutomationOutputFileUploader;
